// ═══════════════════════════════════════════════════════════════
// OpenGov EMC — Disaster Grants Type Definitions
// FEMA Public Assistance & Hazard Mitigation Grant Management
// ═══════════════════════════════════════════════════════════════

// ── Grant Program Types ──────────────────────────────────────
export type GrantProgram = 'pa' | 'hmgp';

// ── Status types (PA) ────────────────────────────────────────

export type OrgStatus = 'active' | 'inactive' | 'pending';
export type OrgType = 'state' | 'county' | 'city' | 'town' | 'township' | 'district' | 'tribal' | 'pnp';
export type OrgRole = 'grantee' | 'subgrantee' | 'applicant';
export type DisasterStatus = 'active' | 'monitoring' | 'closeout' | 'closed';
export type DeclarationType = 'major' | 'emergency' | 'fire_management';
export type DamageCategory = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';
export type DamageStatus = 'identified' | 'inspected' | 'assigned' | 'eligible' | 'ineligible';
export type ProjectSize = 'small' | 'large';
export type ProjectPhase = 'formulation' | 'review' | 'obligation' | 'work_in_progress' | 'closeout' | 'appeal';
export type ProjectStatus = 'draft' | 'in_review' | 'approved' | 'obligated' | 'in_progress' | 'completed' | 'closed' | 'appealed';
export type CostType = 'labor' | 'equipment' | 'materials' | 'contracts' | 'rented_equip' | 'donated' | 'other';
export type ContractStatus = 'active' | 'completed' | 'terminated' | 'pending';
export type InsuranceStatus = 'pending' | 'claim_filed' | 'settled' | 'denied' | 'not_applicable';
export type ComplianceStatus = 'not_started' | 'in_progress' | 'completed' | 'not_required';
export type TimelineEventType = 'milestone' | 'deadline' | 'submission' | 'approval' | 'inspection' | 'meeting' | 'correspondence' | 'task';
export type TimeExtensionStatus = 'draft' | 'submitted' | 'grantee_approved' | 'forwarded_to_fema' | 'approved' | 'denied' | 'revision_needed';
export type RFRStatus = 'draft' | 'submitted' | 'under_review' | 'approved' | 'denied' | 'revision_needed' | 'paid';

// ── Label maps ────────────────────────────────────────────────

export const DAMAGE_CATEGORY_LABELS: Record<DamageCategory, string> = {
  A: 'A — Debris Removal',
  B: 'B — Emergency Protective Measures',
  C: 'C — Roads & Bridges',
  D: 'D — Water Control Facilities',
  E: 'E — Buildings & Equipment',
  F: 'F — Utilities',
  G: 'G — Parks & Recreation',
};

export const DAMAGE_CATEGORY_COLORS: Record<DamageCategory, string> = {
  A: '#92400E', B: '#DC2626', C: '#7C3AED', D: '#0284C7',
  E: '#059669', F: '#D97706', G: '#16A34A',
};

export const PROJECT_PHASE_LABELS: Record<ProjectPhase, string> = {
  formulation: 'Formulation', review: 'Review', obligation: 'Obligation',
  work_in_progress: 'Work in Progress', closeout: 'Closeout', appeal: 'Appeal',
};

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: 'Draft', in_review: 'In Review', approved: 'Approved',
  obligated: 'Obligated', in_progress: 'In Progress', completed: 'Completed',
  closed: 'Closed', appealed: 'Appealed',
};

export const TIME_EXTENSION_STATUS_LABELS: Record<TimeExtensionStatus, string> = {
  draft: 'Draft', submitted: 'Submitted', grantee_approved: 'Grantee Approved',
  forwarded_to_fema: 'Forwarded to FEMA', approved: 'FEMA Approved', denied: 'Denied',
  revision_needed: 'Revision Needed',
};

export const RFR_STATUS_LABELS: Record<RFRStatus, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review',
  approved: 'Approved', denied: 'Denied', revision_needed: 'Revision Needed', paid: 'Paid',
};

export const TIME_EXTENSION_REASONS = [
  'Unforeseen site conditions',
  'Supply chain delays',
  'Permitting/regulatory delays',
  'Weather/seasonal limitations',
  'Contractor availability',
  'Change in scope of work',
  'Environmental/historic compliance review',
  'Insurance settlement pending',
  'Funding/cash flow constraints',
  'Force majeure event',
] as const;

// ── Interfaces ────────────────────────────────────────────────

export interface PAOrganization {
  id: number; Rid: number;
  orgName: string; orgType: string; orgRole: string;
  parentOrgId: string;
  fipsCode: string; femaApplicantId: string;
  ueiNumber: string; samRegistered: string; samExpirationDate: string;
  einTaxId: string; congressionalDistrict: string;
  physicalAddress: string; physicalCity: string; physicalState: string;
  physicalZip: string; physicalCounty: string;
  mailingAddress: string; mailingCity: string; mailingState: string; mailingZip: string;
  primaryContact: string; primaryTitle: string; primaryEmail: string; primaryPhone: string;
  altContact: string; altTitle: string; altEmail: string; altPhone: string;
  garName: string; garEmail: string; garPhone: string;
  scoName: string; spaoName: string; spaoEmail: string;
  contactEmails: string;
  pnpType: string; pnpTaxExemptVerified: string; pnpTaxExemptLetter: string;
  pnpOpenToPublic: string; pnpServicesDescription: string; pnpCriticalFacility: string;
  procurementPolicyOnFile: string; forceAccountPolicyOnFile: string;
  indirectCostRate: string; indirectCostRateType: string;
  singleAuditRequired: string; singleAuditCurrent: string;
  bankName: string; bankRoutingNumber: string; bankAccountNumber: string; bankAccountType: string;
  status: string; notes: string;
}

export interface PADisaster {
  id: number; Rid: number;
  incidentId: string; disasterNumber: string; disasterName: string;
  declarationType: string; declarationDate: string;
  incidentType: string; incidentPeriodStart: string; incidentPeriodEnd: string;
  state: string; designatedCounties: string; paDesignatedAreas: string;
  federalSharePct: string;
  emergencyWorkDeadline: string; permanentWorkDeadline: string;
  largeProjectThreshold: string; smallProjectMinimum: string;
  perCapitaIndicator: string; countyPerCapita: string;
  programDeliveryManagerName: string; programDeliveryManagerEmail: string;
  programDeliveryManagerPhone: string;
  femaRegion: string; cfda: string;
  status: string; notes: string;
}

export interface PADamageItem {
  id: number; Rid: number;
  organizationId: string; disasterId: string;
  facilityName: string; facilityType: string; category: string;
  siteAddress: string; siteCity: string; siteCounty: string;
  siteState: string; siteZip: string;
  lat: string; lng: string;
  preDisasterCondition: string; preDisasterUse: string;
  damageDescription: string; damageCause: string; damageSeverity: string;
  estimatedCost: string; dimensions: string; workStatus: string;
  siteInspected: string; siteInspectionDate: string;
  siteInspectorName: string; siteInspectorOrg: string;
  projectId: string; impactAssessmentId: string;
  status: string; ineligibleReason: string;
  photos: string; notes: string;
}

export interface PAProject {
  id: number; Rid: number;
  organizationId: string; disasterId: string;
  projectNumber: string; projectTitle: string;
  category: string; projectSize: string; workType: string;
  projectAddress: string; projectCity: string; projectCounty: string;
  projectState: string; projectZip: string;
  projectLat: string; projectLng: string;
  damageDescription: string; scopeOfWork: string; costNarrative: string;
  estimatedCost: string; actualCost: string;
  federalSharePct: string; federalShareAmount: string;
  applicantShareAmount: string; insuranceDeduction: string;
  mitigationCost: string; netEligibleCost: string; obligatedAmount: string;
  drawdownAmount: string; pendingReimbursement: string;
  hazardMitigationIncluded: string; hazardMitigationCost: string;
  hazardMitigationDescription: string; hazardMitigationBca: string;
  repairOrReplace: string; improvedProjectElection: string;
  improvedProjectDescription: string;
  preApprovalRequired: string; preApprovalStatus: string;
  completionPctEstimate: string; completionDate: string;
  workStartDate: string; deadline: string;
  extensionRequested: string; extensionNewDeadline: string;
  extensionJustification: string;
  phase: string; status: string;
  versionNumber: string; assignedSpecialist: string;
  femaProjectId: string; cefRequired: string;
  certLegalResponsibility: string; certInsuranceCompliance: string;
  certProcurementCompliance: string; certNoWorkDuplication: string;
  certSignedBy: string; certSignedDate: string;
  notes: string; creatorId: string; createdDate: string;
  lastModifiedBy: string; lastModifiedDate: string;
}

export interface PAProjectCost {
  id: number; Rid: number;
  projectId: string; costType: string; cefPart: string;
  description: string; quantity: string; unit: string;
  unitCost: string; totalCost: string;
  csiCode: string; sourceReference: string;
  estimatedOrActual: string; eligible: string;
  ineligibleReason: string; linkedRecordId: string; notes: string;
}

export interface PALaborRecord {
  id: number; Rid: number;
  projectId: string; organizationId: string;
  employeeName: string; employeeTitle: string; employeeId: string;
  workDate: string; hoursRegular: string; hoursOvertime: string;
  rateRegular: string; rateOvertime: string;
  fringeBenefitsPct: string;
  fringeFica: string; fringeRetirement: string; fringeHealth: string;
  fringeWorkersComp: string; fringeUnemployment: string; fringeOther: string;
  laborCost: string; fringeCost: string; totalCost: string;
  activityDescription: string; category: string;
  timesheetOnFile: string; notes: string; incidentId: string;
  approvedRegHours: string; approvedOTHours: string; approvedCost: string; reviewerNotes: string;
  accountCode: string; programCode: string; shiftPremium: string;
}

export interface PAEquipRecord {
  id: number; Rid: number;
  projectId: string; organizationId: string;
  equipmentType: string; equipmentSize: string;
  femaEquipCode: string; horsepower: string;
  workDate: string; hoursUsed: string;
  femaRate: string; applicantRate: string;
  hourlyRate: string; totalCost: string;
  operatorIncluded: string; operatorName: string;
  activityDescription: string; notes: string;
}

export interface PAMaterial {
  id: number; Rid: number;
  projectId: string; organizationId: string;
  materialDescription: string; vendor: string;
  purchaseDate: string; quantity: string; unit: string;
  unitCost: string; totalCost: string;
  invoiceNumber: string; invoiceOnFile: string;
  fromExistingStock: string; stockValuationMethod: string;
  notes: string;
}

export interface PAContract {
  id: number; Rid: number;
  projectId: string; organizationId: string;
  contractorName: string; contractorUei: string;
  contractNumber: string; contractType: string;
  procurementMethod: string; contractAmount: string;
  changeOrderCount: string; changeOrderAmount: string;
  totalContractValue: string; amountPaid: string; retainageHeld: string;
  scopeOfWork: string; startDate: string; endDate: string;
  costPriceAnalysis: string; conflictOfInterestCheck: string;
  davisBaconApplicable: string; davisBaconCertified: string;
  debarmentChecked: string; debarmentCheckDate: string;
  bondingRequired: string; bondAmount: string;
  competitiveBidsReceived: string; biddersListOnFile: string;
  contractProvisionsIncluded: string; procurementDocsOnFile: string;
  soleSrcJustificationOnFile: string;
  status: string; notes: string;
}

export interface PARentedEquip {
  id: number; Rid: number;
  projectId: string; organizationId: string;
  equipmentType: string; vendor: string;
  rentalAgreementNumber: string;
  rentalStartDate: string; rentalEndDate: string;
  rateType: string; rate: string;
  hoursOrDaysUsed: string; totalCost: string;
  operatorIncluded: string; invoiceOnFile: string;
  activityDescription: string; notes: string;
}

export interface PADonatedResource {
  id: number; Rid: number;
  projectId: string; organizationId: string;
  resourceType: string; donorName: string; donorOrganization: string;
  workDate: string; hoursWorked: string;
  description: string; estimatedValue: string;
  signatureOnFile: string; notes: string;
}

export interface PAInsurance {
  id: number; Rid: number;
  organizationId: string; projectId: string; damageInventoryId: string;
  policyNumber: string; carrier: string; policyType: string;
  coverageAmount: string; deductible: string;
  policyEffectiveDate: string; policyExpirationDate: string;
  claimFiled: string; claimNumber: string; claimDate: string;
  settlementAmount: string; settlementDate: string;
  denialReceived: string; denialReason: string; denialLetterOnFile: string;
  anticipatedRecovery: string; actualRecovery: string;
  femaDeductionAmount: string;
  obtainMaintainRequired: string; obtainMaintainCommitted: string;
  adjusterReportOnFile: string;
  status: string; notes: string;
}

export interface PACompliance {
  id: number; Rid: number;
  projectId: string; organizationId: string;
  nepaRequired: string; nepaLevel: string; nepaStatus: string; nepaCompletionDate: string;
  section106Required: string; section106Status: string;
  historicProperty: string; historicPropertyAge: string;
  shpoConsultation: string; tribalConsultation: string; indianSacredSites: string;
  section106CompletionDate: string;
  inFloodplain: string; floodZone: string; baseFloodElevation: string;
  floodInsuranceRequired: string; floodInsuranceObtained: string;
  eo11988Compliance: string;
  esaConsultationRequired: string; esaStatus: string;
  protectedSpeciesPresent: string; coastalBarrierArea: string;
  hazmatPresent: string; hazmatType: string; hazmatMitigationRequired: string;
  coastalZone: string; coastalZoneConsistency: string;
  wetlandsImpacted: string; wetlandsPermitRequired: string;
  overallComplianceStatus: string; ehpReviewerId: string;
  complianceNotes: string;
}

export interface PADocument {
  id: number; Rid: number;
  organizationId: string; projectId: string;
  damageInventoryId: string; contractId: string; insuranceId: string;
  documentName: string; documentType: string; description: string;
  uploadDate: string; uploadedBy: string;
  fileId: string; fileSize: string; mimeType: string;
  geotagLat: string; geotagLng: string; geotagDate: string;
  tags: string; notes: string;
}

export interface PATimelineEvent {
  id: number; Rid: number;
  projectId: string; organizationId: string; disasterId: string;
  eventType: string; eventDate: string;
  dueDate: string; completedDate: string;
  description: string; creatorId: string;
  status: string; notes: string;
}

export interface PANote {
  id: number; Rid: number;
  parentType: string; parentId: string;
  organizationId: string; noteType: string;
  content: string; creatorId: string;
  createdDate: string; visibility: string;
}

export interface PATimeExtension {
  id: number; Rid: number;
  projectId: string; organizationId: string; disasterId: string;
  requestDate: string; currentDeadline: string;
  requestedNewDeadline: string; extensionMonths: string;
  recipientName: string; recipientTitle: string;
  recipientOrg: string; recipientAddress: string;
  justification: string; supportingCircumstances: string;
  letterContent: string;
  status: string;
  approvalDate: string; approvedBy: string; approvedNewDeadline: string;
  createdBy: string; notes: string;
}

export interface PAReimbursementRequest {
  id: number; Rid: number;
  projectId: string; organizationId: string; disasterId: string;
  requestNumber: string; requestDate: string;
  periodStart: string; periodEnd: string;
  requestedAmount: string; federalShare: string; applicantMatch: string;
  costBreakdown: string; supportingDocIds: string;
  submittedBy: string; submittedDate: string;
  status: string;
  reviewedBy: string; reviewDate: string; reviewNotes: string;
  approvedAmount: string;
  paymentDate: string; paymentReference: string;
  notes: string;
}

// ═══════════════════════════════════════════════════════════════
// HMGP — Hazard Mitigation Grant Program (Section 404)
// ═══════════════════════════════════════════════════════════════

// ── HMGP Status Types ────────────────────────────────────────

export type HMGPProjectType =
  | 'acquisition' | 'elevation' | 'retrofit' | 'safe_room'
  | 'infrastructure' | 'drainage' | 'wildfire_mitigation'
  | 'wind_retrofit' | 'localized_flood_control' | 'generators'
  | 'warning_systems' | 'other';

export type HMGPPhase =
  | 'subapplication' | 'state_review' | 'fema_review'
  | 'selected' | 'awarded' | 'implementation' | 'closeout';

export type HMGPStatus =
  | 'draft' | 'submitted' | 'state_review' | 'state_approved' | 'state_denied'
  | 'fema_review' | 'selected' | 'awarded' | 'in_progress'
  | 'completed' | 'closed' | 'withdrawn';

export type HMGPSubappStatus =
  | 'draft' | 'submitted' | 'under_review' | 'prioritized'
  | 'forwarded_to_fema' | 'returned' | 'withdrawn';

// ── HMGP Label Maps ─────────────────────────────────────────

export const HMGP_PROJECT_TYPE_LABELS: Record<HMGPProjectType, string> = {
  acquisition: 'Acquisition / Buyout',
  elevation: 'Elevation',
  retrofit: 'Structural Retrofit',
  safe_room: 'Safe Room / Shelter',
  infrastructure: 'Infrastructure Protection',
  drainage: 'Drainage Improvement',
  wildfire_mitigation: 'Wildfire Mitigation',
  wind_retrofit: 'Wind Retrofit',
  localized_flood_control: 'Localized Flood Control',
  generators: 'Generators',
  warning_systems: 'Warning Systems',
  other: 'Other',
};

export const HMGP_PROJECT_TYPE_COLORS: Record<HMGPProjectType, string> = {
  acquisition: '#0284C7',
  elevation: '#7C3AED',
  retrofit: '#D97706',
  safe_room: '#DC2626',
  infrastructure: '#059669',
  drainage: '#0E6F7F',
  wildfire_mitigation: '#92400E',
  wind_retrofit: '#6366F1',
  localized_flood_control: '#0891B2',
  generators: '#4B5563',
  warning_systems: '#B45309',
  other: '#6B7280',
};

export const HMGP_PHASE_LABELS: Record<HMGPPhase, string> = {
  subapplication: 'Subapplication',
  state_review: 'State Review',
  fema_review: 'FEMA Review',
  selected: 'Selected',
  awarded: 'Awarded',
  implementation: 'Implementation',
  closeout: 'Closeout',
};

export const HMGP_PHASE_COLORS: Record<HMGPPhase, string> = {
  subapplication: 'blue',
  state_review: 'yellow',
  fema_review: 'orange',
  selected: 'teal',
  awarded: 'green',
  implementation: 'violet',
  closeout: 'gray',
};

export const HMGP_STATUS_LABELS: Record<HMGPStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  state_review: 'State Review',
  state_approved: 'State Approved',
  state_denied: 'State Denied',
  fema_review: 'FEMA Review',
  selected: 'Selected',
  awarded: 'Awarded',
  in_progress: 'In Progress',
  completed: 'Completed',
  closed: 'Closed',
  withdrawn: 'Withdrawn',
};

export const HMGP_SUBAPP_STATUS_LABELS: Record<HMGPSubappStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  prioritized: 'Prioritized',
  forwarded_to_fema: 'Forwarded to FEMA',
  returned: 'Returned',
  withdrawn: 'Withdrawn',
};

// ── HMGP Interfaces ──────────────────────────────────────────

export interface HMGPProject {
  id: number; Rid: number;
  organizationId: string; disasterId: string;
  projectNumber: string; projectTitle: string;
  projectType: string; // HMGPProjectType
  projectAddress: string; projectCity: string; projectCounty: string;
  projectState: string; projectZip: string;
  projectLat: string; projectLng: string;
  problemDescription: string; scopeOfWork: string;
  estimatedCost: string; federalSharePct: string;
  federalShareAmount: string; applicantShareAmount: string;
  otherFunding: string; otherFundingSource: string;
  // BCA fields
  bcaRequired: string; bcaMethodology: string;
  bcaRatio: string; pvBenefits: string; pvCosts: string;
  usefulLife: string; discountRate: string;
  bcaToolVersion: string; bcaCompletedDate: string;
  // Environmental / Historic Preservation
  ehpStatus: string; ehpCompletedDate: string;
  nepaLevel: string; nepaStatus: string;
  section106Required: string; section106Status: string;
  // Property-specific (for acquisition/elevation)
  numberOfStructures: string; structureType: string;
  preDisasterValue: string; floodZone: string;
  repetitiveLoss: string; severeRepetitiveLoss: string;
  // Tracking
  subapplicationId: string; subapplicationDate: string;
  statePriorityRank: string;
  femaAppNumber: string; femaAwardDate: string;
  awardAmount: string; obligatedAmount: string; drawdownAmount: string;
  performancePeriodStart: string; performancePeriodEnd: string;
  quarterlyReportDue: string;
  phase: string; status: string;
  assignedSpecialist: string;
  notes: string; creatorId: string; createdDate: string;
  lastModifiedBy: string; lastModifiedDate: string;
}

export interface HMGPSubapplication {
  id: number; Rid: number;
  organizationId: string; disasterId: string;
  subappNumber: string; subappTitle: string;
  projectType: string; // HMGPProjectType
  problemStatement: string; proposedActivity: string;
  estimatedCost: string; federalRequest: string; nonFederalMatch: string;
  matchSource: string;
  // Location
  projectAddress: string; projectCity: string; projectCounty: string;
  projectState: string; projectLat: string; projectLng: string;
  // BCA summary
  bcaStatus: string; bcaRatio: string;
  // Priority
  statePriorityScore: string; statePriorityRank: string;
  // Status tracking
  submittedDate: string; submittedBy: string;
  reviewerName: string; reviewDate: string; reviewNotes: string;
  status: string; // HMGPSubappStatus
  linkedProjectId: string; // created HMGPProject Rid when forwarded
  notes: string; creatorId: string; createdDate: string;
}
